/**
 * Privacy-friendly visitor analytics for 6amico (GDPR).
 *
 * First-party only: eventi anonimi POST /api/analytics/event → MongoDB
 * (dashboard admin). Nessuno script/beacon di terze parti (GA4 e Cloudflare
 * Web Analytics rimossi): nessun dato esce dal nostro backend.
 *
 * Geo (region/country) viene derivato server-side dagli header della
 * richiesta (cf-ipcountry / x-country-code) — niente API geo-IP lato client.
 */

import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { pathLang } from "./langRoute";

const CONSENT_LS_KEY = "6amico_analytics_consent";

function analyticsApiBase() {
  const raw = (process.env.REACT_APP_BACKEND_URL || "").trim().replace(/\/+$/, "");
  if (!raw) return "/api";
  if (/^https?:\/\//i.test(raw)) return `${raw}/api`;
  return `http://${raw.replace(/^\/+/, "")}/api`;
}

/** Canonical section ids for `section_click` events. */
export const ANALYTICS_SECTIONS = Object.freeze({
  monumenti: "monumenti",
  sentieri_cai: "sentieri_cai",
  aree_camper: "aree_camper",
  mappa: "mappa",
  percorsi: "percorsi",
  frazioni: "frazioni",
  mangiare: "mangiare",
  dormire: "dormire",
  prodotti: "prodotti",
  luoghi: "luoghi",
  natura: "natura",
  proloco: "proloco",
  storia: "storia",
  mare: "mare",
  terra: "terra",
  montagna: "montagna",
  bici: "bici",
  cultura: "cultura",
});

const TAB_TO_SECTION = {
  luoghi: ANALYTICS_SECTIONS.monumenti,
  "cosa-vedere": ANALYTICS_SECTIONS.monumenti,
  musei: ANALYTICS_SECTIONS.monumenti,
  percorsi: ANALYTICS_SECTIONS.sentieri_cai,
  natura: ANALYTICS_SECTIONS.natura,
  frazioni: ANALYTICS_SECTIONS.frazioni,
  mangiare: ANALYTICS_SECTIONS.mangiare,
  dormire: ANALYTICS_SECTIONS.dormire,
  prodotti: ANALYTICS_SECTIONS.prodotti,
  proloco: ANALYTICS_SECTIONS.proloco,
  storia: ANALYTICS_SECTIONS.storia,
  sentieri: ANALYTICS_SECTIONS.sentieri_cai,
  MONUMENTI: ANALYTICS_SECTIONS.monumenti,
  BIKE: ANALYTICS_SECTIONS.bici,
  HOTEL: ANALYTICS_SECTIONS.dormire,
};

let initialized = false;

function readStoredConsent() {
  try {
    const v = localStorage.getItem(CONSENT_LS_KEY);
    if (v === "1" || v === "true") return true;
    if (v === "0" || v === "false") return false;
  } catch {
    /* ignore */
  }
  return null;
}

/** Default allow first-party metrics; explicit opt-out (0) is respected. */
function analyticsConsentGranted() {
  return readStoredConsent() !== false;
}

/** Call once on app boot (kept for API compat — first-party, nothing to inject). */
export function initAnalytics() {
  initialized = true;
}

export function setAnalyticsConsent(granted) {
  try {
    localStorage.setItem(CONSENT_LS_KEY, granted ? "1" : "0");
  } catch {
    /* ignore */
  }
}

/**
 * Map hub tab / map filter / layer id → canonical section for dashboards.
 * @param {string} raw
 */
export function normalizeSectionId(raw) {
  if (!raw) return "unknown";
  const key = String(raw);
  if (TAB_TO_SECTION[key]) return TAB_TO_SECTION[key];
  const lower = key.toLowerCase();
  if (ANALYTICS_SECTIONS[lower]) return ANALYTICS_SECTIONS[lower];
  if (TAB_TO_SECTION[lower]) return TAB_TO_SECTION[lower];
  if (lower === "sentieri" || lower.includes("sentier")) return ANALYTICS_SECTIONS.sentieri_cai;
  if (lower.includes("camper")) return ANALYTICS_SECTIONS.aree_camper;
  if (lower.includes("monument")) return ANALYTICS_SECTIONS.monumenti;
  return lower.replace(/\s+/g, "_").slice(0, 64);
}

/**
 * Fire-and-forget POST to 6amico metrics (GDPR: no PII).
 * Used so admin Analytics can read section/monument breakdowns.
 */
export function postAnalyticsEvent(payload) {
  if (!payload || typeof payload !== "object" || typeof fetch === "undefined") return;
  if (!analyticsConsentGranted()) return;
  const body = { ...payload };
  delete body.email;
  delete body.user_id;
  delete body.name;
  try {
    fetch(`${analyticsApiBase()}/analytics/event`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => null);
  } catch {
    /* ignore */
  }
}

let sessionSourceSent = false;

/**
 * Fonte di arrivo della sessione (privacy-first): solo il dominio del referrer
 * e i tag utm_* del primo URL — mai path/query del referrer né dati personali.
 * Inviata una sola volta, sulla prima page_view della sessione SPA.
 */
function sessionSource() {
  if (sessionSourceSent || typeof document === "undefined") return {};
  sessionSourceSent = true;
  const out = {};
  try {
    const ref = document.referrer;
    if (ref) {
      const host = new URL(ref).hostname;
      if (host && host !== window.location.hostname) {
        out.referrer_domain = host.slice(0, 120);
      }
    }
    const params = new URLSearchParams(window.location.search);
    for (const key of ["utm_source", "utm_medium", "utm_campaign"]) {
      const v = (params.get(key) || "").trim();
      if (v) out[key] = v.slice(0, 120);
    }
  } catch {
    /* ignore */
  }
  return out;
}

export function trackPageView(path) {
  const pathname = String(path || "").split("?")[0];
  postAnalyticsEvent({
    kind: "page_view",
    entity_type: "page",
    path: String(path || "").slice(0, 500),
    lang: pathLang(pathname) || "it",
    ...sessionSource(),
  });
}

/**
 * Generic engagement event → backend metric (kind territory_engagement).
 * Fires only when the event has a territorial context (comune/monument/section),
 * otherwise it's a no-op — no third-party tracking exists anymore.
 */
export function trackEvent(name, params = {}) {
  const safe = { ...params };
  delete safe.email;
  delete safe.user_id;
  delete safe.name;
  const comuneSlug = safe.comune_slug || safe.comuneSlug;
  const monumentId = safe.monument_id || safe.monumentId;
  const section = safe.section ? normalizeSectionId(safe.section) : undefined;
  if (!comuneSlug && !monumentId && !section) return;
  postAnalyticsEvent({
    kind: "territory_engagement",
    entity_type: monumentId ? "monument" : "section",
    entity_slug: comuneSlug || undefined,
    entity_id: monumentId || undefined,
    comune_slug: comuneSlug || undefined,
    monument_id: monumentId || undefined,
    section,
    label: name ? String(name).slice(0, 200) : undefined,
    path:
      typeof window !== "undefined"
        ? `${window.location.pathname}${window.location.search || ""}`.slice(0, 500)
        : undefined,
  });
}

/**
 * Territory engagement → backend metrics.
 * Prefer this inside comune/monument UI so the dashboard can unpack visits.
 *
 * @param {{ comuneSlug?: string, monumentId?: string, section?: string, label?: string, path?: string, source?: string }} opts
 */
export function trackTerritoryEngagement(opts = {}) {
  const {
    comuneSlug,
    monumentId,
    section,
    label,
    path,
    source,
  } = opts;
  const sec = normalizeSectionId(
    section || (monumentId ? ANALYTICS_SECTIONS.monumenti : "unknown"),
  );
  const pathVal =
    path ||
    (typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search || ""}`
      : undefined);

  const kind = monumentId ? "monument_view" : "section_click";
  postAnalyticsEvent({
    kind,
    entity_type: monumentId ? "monument" : "section",
    entity_slug: comuneSlug || undefined,
    entity_id: monumentId || undefined,
    comune_slug: comuneSlug || undefined,
    monument_id: monumentId || undefined,
    section: sec,
    label: (label || source) ? String(label || source).slice(0, 200) : undefined,
    path: pathVal ? String(pathVal).slice(0, 500) : undefined,
  });
}

/** High-traffic CTA / tab / map channel click (backend metric when territorial). */
export function trackSectionClick(section, extra = {}) {
  const sec = normalizeSectionId(section);
  const comuneSlug = extra.comune_slug || extra.comuneSlug;
  const monumentId = extra.monument_id || extra.monumentId;
  if (comuneSlug || monumentId) {
    postAnalyticsEvent({
      kind: monumentId ? "monument_view" : "section_click",
      entity_type: monumentId ? "monument" : "section",
      entity_slug: comuneSlug || undefined,
      entity_id: monumentId || undefined,
      comune_slug: comuneSlug || undefined,
      monument_id: monumentId || undefined,
      section: sec,
      label: extra.label ? String(extra.label).slice(0, 200) : undefined,
      path:
        typeof window !== "undefined"
          ? `${window.location.pathname}${window.location.search || ""}`.slice(0, 500)
          : undefined,
    });
  }
}

/**
 * SPA page views on React Router navigation.
 */
export function AnalyticsRouteListener() {
  const location = useLocation();

  useEffect(() => {
    initAnalytics();
  }, []);

  useEffect(() => {
    const path = `${location.pathname}${location.search}`;
    trackPageView(path);
  }, [location.pathname, location.search]);

  return null;
}

/**
 * Visible dwell time on monument boards / comune pages.
 * Fires `monument_engagement` or `comune_board_time` on unmount / hide (≥1s).
 */
export function useBoardEngagement(eventName, params = {}) {
  const paramsKey = JSON.stringify(params || {});

  useEffect(() => {
    let accumulated = 0;
    let visibleSince =
      document.visibilityState === "visible" ? Date.now() : null;

    const flushVisible = () => {
      if (visibleSince != null) {
        accumulated += Date.now() - visibleSince;
        visibleSince = null;
      }
    };

    const onVis = () => {
      if (document.visibilityState === "hidden") {
        flushVisible();
      } else if (visibleSince == null) {
        visibleSince = Date.now();
      }
    };

    const send = () => {
      flushVisible();
      const seconds = Math.round(accumulated / 1000);
      if (seconds < 1 || !eventName) return;
      let parsed = {};
      try {
        parsed = JSON.parse(paramsKey);
      } catch {
        parsed = {};
      }
      trackEvent(eventName, {
        ...parsed,
        engagement_seconds: seconds,
      });
    };

    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pagehide", send);

    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pagehide", send);
      send();
    };
  }, [eventName, paramsKey]);
}

export function isAnalyticsConfigured() {
  return true;
}

export function getAnalyticsProviders() {
  return {
    backend: true,
    ga4: false,
    cloudflare: false,
  };
}
